import React, { useState, useMemo } from 'react';
import { OrderDetailsDTO } from '../../types/order';
import StatusChip from '../shared/StatusChip';
import PriorityBadge from '../shared/PriorityBadge';
import { getPhotoUrl } from '../../utils/photoUtils';

interface OrderHeroProps {
  order: OrderDetailsDTO;
  onAddPrice: () => void;
}

const OrderHero: React.FC<OrderHeroProps> = ({ order, onAddPrice }) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  
  // Get primary photo or placeholder - normalize URLs
  const validPhotos = useMemo(() => {
    return order.photos
      .map(photo => ({
        ...photo,
        normalizedUrl: getPhotoUrl(photo)
      }))
      .filter(photo => photo.normalizedUrl !== null);
  }, [order.photos]);
  
  const primaryPhoto = validPhotos.length > 0 
    ? validPhotos[selectedPhotoIndex % validPhotos.length] 
    : null;
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 p-6 sm:p-8">
        {/* Photo Carousel - Enhanced */}
        <div className="space-y-4">
          <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden shadow-inner border border-gray-200">
            {primaryPhoto && primaryPhoto.normalizedUrl ? (
              <img
                src={primaryPhoto.normalizedUrl}
                alt={`Order photo ${selectedPhotoIndex + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center text-gray-400">
                        <div class="text-center">
                          <svg class="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p class="text-sm font-medium">Failed to load photo</p>
                        </div>
                      </div>
                    `;
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <svg className="w-20 h-20 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-500">No photos yet</p>
                  <p className="text-xs text-gray-400 mt-1">Upload photos to get started</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Thumbnails - Enhanced */}
          {validPhotos.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
              {validPhotos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => setSelectedPhotoIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedPhotoIndex === index 
                      ? 'border-blue-600 ring-2 ring-blue-200 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={photo.normalizedUrl!}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Quick Facts Card - Enhanced */}
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Facts</h2>
            <div className="space-y-5">
              {/* Customer */}
              <div className="pb-4 border-b border-gray-100">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Customer</label>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">
                      {order.customer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{order.customer.name}</p>
                    {order.customer.phone && (
                      <a
                        href={`tel:${order.customer.phone}`}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {order.customer.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Assigned Tailor */}
              {order.assignedTailor && (
                <div className="pb-4 border-b border-gray-100">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Assigned Tailor</label>
                  <p className="font-semibold text-gray-900">{order.assignedTailor.name}</p>
                </div>
              )}
              
              {/* Due Date & Priority */}
              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Due Date</label>
                  <p className="font-semibold text-gray-900">
                    {order.dueAt 
                      ? new Date(order.dueAt).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })
                      : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Priority</label>
                  <div>
                    <PriorityBadge priority={order.priority} />
                  </div>
                </div>
              </div>
              
              {/* Status */}
              <div className="pb-4 border-b border-gray-100">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Status</label>
                <div>
                  <StatusChip status={order.status} />
                </div>
              </div>
              
              {/* Price Summary */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 block">Price Summary</label>
                {order.price ? (
                  <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                    {order.price.materials && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Materials</span>
                        <span className="font-semibold text-gray-900">{order.price.currency} {order.price.materials.toFixed(2)}</span>
                      </div>
                    )}
                    {order.price.labor && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Labor</span>
                        <span className="font-semibold text-gray-900">{order.price.currency} {order.price.labor.toFixed(2)}</span>
                      </div>
                    )}
                    {order.price.total && (
                      <div className="flex justify-between text-base pt-3 mt-3 border-t border-gray-200">
                        <span className="font-bold text-gray-900">Total</span>
                        <span className="font-bold text-blue-600 text-lg">{order.price.currency} {order.price.total.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-2">
                    <button
                      onClick={onAddPrice}
                      className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
                    >
                      Add price
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHero;

